import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ProductCard from './ProductCard';
import Icon from '../../../components/AppIcon';

const ProductGrid = ({ 
  products, 
  viewMode, 
  loading, 
  onViewModeChange, 
  onResetFilters, 
  onProductSelect,
  onCategoryChange,
  categoryPath = [{ id: 'all', name: 'All Products' }]
}) => {
  const navigate = useNavigate();


  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading Skeleton */}
        <div className="flex items-center justify-between">
          <div className="h-6 bg-muted rounded w-32 animate-pulse"></div>
          <div className="h-10 bg-muted rounded w-40 animate-pulse"></div>
        </div>
        <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5' : 'grid-cols-1'}`}>
          {[...Array(10)]?.map((_, index) => (
            <div key={index} className="bg-card border border-border rounded-lg overflow-hidden animate-pulse">
              <div className="h-48 bg-muted"></div>
              <div className="p-4 space-y-3">
                <div className="h-6 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-full"></div>
                <div className="h-4 bg-muted rounded w-2/3"></div>
                <div className="flex justify-between items-center pt-3">
                  <div className="h-6 bg-muted rounded w-20"></div>
                  <div className="h-8 bg-muted rounded w-24"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (products?.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-16"
      >
        <div className="max-w-md mx-auto">
          <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="Search" size={32} className="text-text-secondary" />
          </div>
          <h3 className="text-xl font-semibold text-text-primary mb-2">No Products Found</h3>
          <p className="text-text-secondary mb-6">
            We couldn't find any products matching your current filters. Try adjusting your search criteria or clearing some filters.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => (onResetFilters?.() ?? window.location.reload())}
              className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200"
            >
              <Icon name="RotateCcw" size={16} className="mr-2" />
              Reset Filters
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="inline-flex items-center justify-center px-4 py-2 border border-border text-text-primary rounded-lg hover:bg-muted transition-colors duration-200"
            >
              <Icon name="MessageCircle" size={16} className="mr-2" />
              Contact Us
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Results Header */}
      <div className="flex flex-row items-end justify-between gap-2 md:gap-4 border-b border-border pb-6">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {categoryPath?.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <h2 
                  onClick={() => onCategoryChange?.(item.id)}
                  className={`tracking-tight transition-colors cursor-pointer ${
                    index === 0 ? 'text-xl md:text-2xl font-black' : 'text-base md:text-lg font-medium mt-0.5'
                  } ${
                    index === categoryPath.length - 1 ? 'text-text-primary' : 'text-text-secondary/50 hover:text-primary'
                  }`}
                >
                  {item.name}
                </h2>
                {index < categoryPath.length - 1 && (
                  <Icon name="ChevronRight" size={18} className="text-text-secondary/30" />
                )}
              </div>
            ))}
          </div>
          <p className="text-text-secondary text-sm">
            Showing <span className="font-bold text-primary">{products?.length}</span> premium products
          </p>
        </div>
        
        <div className="flex flex-shrink-0 items-center justify-end gap-4">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-muted/50 border border-border/50 rounded-xl p-1 shadow-inner">
            <button
              onClick={() => onViewModeChange('grid')}
              className={`p-2 rounded transition-colors duration-200 ${
                viewMode === 'grid' ? 'bg-background text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'
              }`}
              aria-label="Grid view"
            >
              <Icon name="Grid3x3" size={16} />
            </button>
            <button
              onClick={() => onViewModeChange('list')}
              className={`p-2 rounded transition-colors duration-200 ${
                viewMode === 'list' ? 'bg-background text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'
              }`}
              aria-label="List view"
            >
              <Icon name="List" size={16} />
            </button>
          </div>


        </div>
      </div>
      {/* Product Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`grid gap-6 ${
          viewMode === 'grid' ?'grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5' :'grid-cols-1'
        }`}
      >
        {products?.map((product, index) => (
          <motion.div
            key={product?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <ProductCard 
              product={product} 
              viewMode={viewMode} 
              onSelect={onProductSelect}
            />
          </motion.div>
        ))}
      </motion.div>
      {/* Load More Button (if needed) */}
      {products?.length >= 12 && (
        <div className="text-center pt-8">
          <button className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200">
            <Icon name="Plus" size={16} className="mr-2" />
            Load More Products
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;